import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { contactApi, type RepView } from "../api/contact";

const SEEN_KEY = "salesRepPopupSeen";

const HelpIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 0 1 5.82 1c0 2-3 3-3 3" /><path d="M12 17h.01" />
  </svg>
);
const MailIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" />
  </svg>
);
const PhoneIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.1 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

/**
 * Dismissible floating card nudging the user to contact their sales rep.
 * Appears once per session a few seconds after landing on a catalog page;
 * pulls the assigned rep (name/email/phone) from /contact/principal.
 */
export function SalesRepPopup() {
  const { t } = useTranslation();
  const [rep, setRep] = useState<RepView | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SEEN_KEY)) return;
    const timer = setTimeout(() => {
      void contactApi.getPrincipal()
        .then((c) => {
          const r = c.salesReps[0] ?? c.qualityRep ?? null;
          if (r) {
            setRep(r);
            setVisible(true);
            sessionStorage.setItem(SEEN_KEY, "1");
          }
        })
        .catch(() => undefined);
    }, 7000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible || !rep) return null;

  return (
    <div className="sales-pop" style={wrap} role="dialog" aria-label={t("catalog.salesPopup.title", "Have questions?")}>
      <div style={header}>
        <span style={headerIcon}><HelpIcon /></span>
        <span style={{ fontWeight: 700, fontSize: 14.5, color: "var(--on-primary)", flex: 1, letterSpacing: "-0.01em" }}>
          {t("catalog.salesPopup.title", "Have questions?")}
        </span>
        <button type="button" className="sales-pop-close" onClick={() => setVisible(false)} aria-label="Close" style={closeBtn}>×</button>
      </div>

      <div style={body}>
        <div style={{ color: "var(--ink-3)", fontSize: 12.5 }}>{t("catalog.salesPopup.body", "Ask your sales representative:")}</div>

        <div style={{ display: "flex", alignItems: "center", gap: 13, marginTop: 12 }}>
          {rep.avatarUrl ? (
            <img src={rep.avatarUrl} alt="" style={avatar} />
          ) : (
            <div style={{ ...avatar, display: "grid", placeItems: "center", background: "var(--brand-primary)", color: "var(--on-primary)", fontWeight: 700, fontSize: 19 }}>
              {rep.displayName.slice(0, 1).toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0, fontWeight: 700, color: "var(--ink)", fontSize: 15.5, letterSpacing: "-0.01em" }}>
            {rep.displayName}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 12 }}>
          <a href={`mailto:${rep.email}`} className="sales-rep-row" style={row}>
            <span style={rowIcon}><MailIcon /></span>
            <span style={rowText}>{rep.email}</span>
          </a>
          {rep.phone && (
            <a href={`tel:${rep.phone.replace(/\s+/g, "")}`} className="sales-rep-row" style={row}>
              <span style={rowIcon}><PhoneIcon /></span>
              <span style={rowText}>{rep.phone}</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

const wrap: CSSProperties = {
  position: "fixed", right: 20, bottom: 20, zIndex: 55, width: 326, maxWidth: "calc(100vw - 32px)",
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md)",
  overflow: "hidden", boxShadow: "var(--shadow-2)",
};
const header: CSSProperties = {
  display: "flex", alignItems: "center", gap: 10, padding: "13px 14px",
  background: "linear-gradient(135deg, var(--brand-primary), var(--brand-ink))",
};
const headerIcon: CSSProperties = { display: "grid", placeItems: "center", color: "var(--on-primary)", opacity: 0.92, flex: "none" };
const closeBtn: CSSProperties = {
  flex: "none", width: 26, height: 26, borderRadius: 7, border: "none", cursor: "pointer",
  background: "color-mix(in srgb, var(--on-primary) 16%, transparent)", color: "var(--on-primary)", fontSize: 17, lineHeight: 1,
  display: "grid", placeItems: "center",
};
const body: CSSProperties = { padding: "14px 16px 16px" };
const avatar: CSSProperties = {
  width: 52, height: 52, borderRadius: "50%", objectFit: "cover", flex: "none",
  border: "2px solid var(--surface)", boxShadow: "0 0 0 2px var(--brand-primary)",
};
const row: CSSProperties = {
  display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8,
  textDecoration: "none", color: "var(--brand-primary)", fontSize: 13, fontWeight: 600,
};
const rowIcon: CSSProperties = { display: "grid", placeItems: "center", color: "var(--brand-primary)", flex: "none" };
const rowText: CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
